import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { movieBookingApi } from '../../services/movieBookingApi';
import { getMovieBookingRole, isAdminRole } from './movieBookingRole';
import './MovieBooking.css';

const AdminDashboard = ({ onSwitchRole }) => {
  const { user } = useAuth();
  const hasBackendAdmin = isAdminRole(user);
  const hasSelectedAdmin = getMovieBookingRole() === 'ADMIN';
  const isAdmin = hasBackendAdmin || hasSelectedAdmin;
  const [theatres, setTheatres] = useState([]);
  const [movies, setMovies] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdminData = async () => {
      try {
        const [theatresResponse, moviesResponse, showsResponse] = await Promise.all([
          movieBookingApi.getAdminTheatres(),
          movieBookingApi.getMovies(),
          movieBookingApi.getAdminShows(),
        ]);
        setTheatres(theatresResponse.data || []);
        setMovies(moviesResponse.data || []);
        setShows(showsResponse.data || []);
      } catch (error) {
        setTheatres([]);
        setMovies([]);
        setShows([]);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) {
      loadAdminData();
    }
  }, [isAdmin]);

  const movieTitleById = useMemo(() => {
    const map = new Map();
    movies.forEach((movie) => {
      map.set(movie.id, movie.title || movie.id);
    });
    return map;
  }, [movies]);

  const showsByTheatreId = useMemo(() => {
    return shows.reduce((acc, show) => {
      if (!acc[show.theatreId]) {
        acc[show.theatreId] = [];
      }
      acc[show.theatreId].push(show);
      return acc;
    }, {});
  }, [shows]);

  if (!isAdmin) {
    return (
      <div className="movie-booking-page">
        <h1 className="movie-booking-title">Admin access required</h1>
        <p className="movie-booking-note">Switch to User role to browse and book tickets.</p>
        <Link className="movie-booking-button secondary" to="/movies">Go to Movie Booking</Link>
      </div>
    );
  }

  return (
    <div className="movie-booking-page">
      <div className="movie-booking-header">
        <div>
          <h1 className="movie-booking-title">Movie Booking Admin</h1>
          <p className="movie-booking-note">Manage theatres, shows, and seat inventory.</p>
        </div>
        {onSwitchRole && (
          <button className="movie-booking-button secondary" onClick={onSwitchRole}>
            Switch Role
          </button>
        )}
      </div>

      <div className="movie-booking-card-grid">
        <div className="movie-booking-card">
          <h3>Create Movie</h3>
          <p className="movie-booking-note">Add a new movie to the catalog.</p>
          <Link className="movie-booking-button" to="/movies/admin/movies">Create Movie</Link>
        </div>
        <div className="movie-booking-card">
          <h3>Create Theatre</h3>
          <p className="movie-booking-note">Add a new theatre in your city.</p>
          <Link className="movie-booking-button" to="/movies/admin/theatres">Create Theatre</Link>
        </div>
        <div className="movie-booking-card">
          <h3>Create Show</h3>
          <p className="movie-booking-note">Add a show and initialize seats.</p>
          <Link className="movie-booking-button" to="/movies/admin/shows">Create Show</Link>
        </div>
      </div>

      <h2 className="movie-booking-title" style={{ marginTop: '2rem' }}>Your Theatres</h2>
      {loading && <p>Loading theatres...</p>}
      {!loading && (
        <div className="movie-booking-card-grid">
          {theatres.map((theatre) => (
            <div key={theatre.id} className="movie-booking-card">
              <h3>{theatre.name}</h3>
              <p className="movie-booking-note">City: {theatre.city}</p>
              <Link className="movie-booking-button secondary" to={`/movies/admin/shows?theatreId=${encodeURIComponent(theatre.id)}`}>
                Add Show
              </Link>
              {!!showsByTheatreId[theatre.id]?.length && (
                <div style={{ marginTop: '1rem' }}>
                  <p className="movie-booking-note">Shows:</p>
                  {showsByTheatreId[theatre.id].map((show) => (
                    <p key={show.id} className="movie-booking-note">
                      {movieTitleById.get(show.movieId) || show.movieId} • {show.showDate} {show.startTime} - {show.endTime} • {show.screenId}
                      {' '}
                      <Link className="movie-booking-button secondary" to={`/movies/admin/shows/${show.id}/seats`}>
                        View Seats
                      </Link>
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!theatres.length && <p>No theatres created yet.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
